import { createContext } from 'react';
import type { Badge } from '../types/Badge';
import type { BadgeActivity } from '../types/Activity';
import type { Notification as NotificationType } from '../types/Notification';
import type { HomeBrewReview } from '../types/HomeBrewReview';
import type { FlavorProfile } from '../utils/flavorAnalysis';

// This file ONLY contains type definitions and context creation
// NO Firebase imports - safe to import anywhere

export interface AppUser {
  id: string;
  uid?: string;
  name: string;
  displayName: string;
  email: string;
  photo: string;
  isAdmin?: boolean;
  checkInPhotos: Record<string, string>;
  badges: Badge[];
  activities: BadgeActivity[];
  location?: {
    lat: number;
    lng: number;
  };
  flavorProfile?: FlavorProfile;
  /**
   * Account creation time. Shaped to match a Firestore Timestamp (`.toDate()`)
   * without importing firebase types here, since this file is kept
   * Firebase-free so it's safe to import anywhere. Also accepts a plain ISO
   * string for callers that have already normalized it.
   */
  createdAt?: { toDate: () => Date } | string;
}

export interface UserContextType {
  user: AppUser | null;
  loading: boolean;
  setUser: React.Dispatch<React.SetStateAction<AppUser | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setNotifications: React.Dispatch<React.SetStateAction<NotificationType[]>>;
  login: (userData: AppUser) => void;
  logout: () => void;
  badges: Badge[];
  addBadge: (badge: Badge) => void;
  activities: BadgeActivity[];
  addActivity: (activity: BadgeActivity) => void;
  notifications: NotificationType[];
  addNotification: (notification: NotificationType | NotificationType[]) => void;
  markAllNotificationsRead: () => void;
  deleteAllNotifications: () => void;
  updatePhoto: (photoUrl: string) => void;
  startNotificationsListener: () => void;
  stopNotificationsListener: () => void;
  pushSubscription: PushSubscription | null;
  notificationsEnabled: boolean;
  setNotificationsEnabled: (value: boolean) => void;
  enableNotifications: () => Promise<void>;
  disableNotifications: () => Promise<void>;
  updateFlavorProfile: (reviews: HomeBrewReview[]) => void;
  showBadgeDialog: (badgeIds: string[]) => void;
}

// Create context - can be imported by both UserContext.tsx and useUser.ts
export const UserContext = createContext<UserContextType | null>(null);
